import { useState } from "react";
import { Button, Collapse } from "react-bootstrap";
import { ChevronDown, ChevronRight } from "react-bootstrap-icons";
import { getFromDataPath } from "../../utility";
import { useArsContext } from "../contexts";
import { isWhereClause } from "../typeGuards";
import styles from "./WhereClauseConditionItemControl.module.css";
import { WhereClauseEditor } from "./WhereClauseEditor";
import { WhereClauseInput } from "./WhereClauseInput";

/**
 * Props for the WhereClauseManager component.
 */
export interface WhereClauseManagerProps {
  /** RFC 6901 JSON Pointer path to the entity (AnalysisSet, DataSubset, or Group) in ArsContext state */
  dataPath: string;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Manages where clause conditions for entities (AnalysisSet, DataSubset, or Group).
 *
 * Shows sub-components for editing and displaying where clauses.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the entity in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the where clause manager.
 */
export const WhereClauseManager = ({
  dataPath,
  disabled = false,
}: WhereClauseManagerProps): React.ReactElement => {
  const { state } = useArsContext();
  const [isExpanded, setIsExpanded] = useState(false);

  // Get the current entity from state
  const whereClause = getFromDataPath(dataPath, state, isWhereClause);

  const idPath = `${dataPath.replace(/\//g, "-")}`;

  return !whereClause ? (
    <span className="text-danger">Entity not found at path: {dataPath}</span>
  ) : (
    <div>
      {/* Compact view */}
      <div className={styles.compactContainer}>
        <Button
          id={`${idPath}-toggle-expand`}
          onClick={() => setIsExpanded(!isExpanded)}
          className={styles.toggleButton}
          variant="link"
          size="sm"
          aria-label={isExpanded ? "Collapse" : "Expand"}
          disabled={disabled}
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Button>
        <div
          className="flex-grow-1"
          style={{ minWidth: 0 }}
        >
          <WhereClauseInput
            dataPath={dataPath}
            disabled={disabled}
          />
        </div>
      </div>
      {/* Expanded view */}
      <Collapse in={isExpanded}>
        <div>
          <WhereClauseEditor
            dataPath={dataPath}
            disabled={disabled}
          />
        </div>
      </Collapse>
    </div>
  );
};

WhereClauseManager.displayName = "WhereClauseManager";
